import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Submission, TableColumn } from '../../types/domain';
import { RBACPermissionTier } from '../../types/grid';
import { RawJSONViewer } from './RawJSONViewer';
import {
  X,
  ChevronDown,
  ChevronUp,
  Trash2,
  Save,
  ShieldAlert,
  ShieldCheck,
  Clock,
  Globe,
  Radio,
  Paperclip,
  FileText,
  Image as ImageIcon,
  Download,
  ExternalLink,
  Copy,
  Check,
  Code,
  Brackets,
  Eye,
} from 'lucide-react';
import { isFileAttachment, formatFileSize, resolveFileGatewayUrl } from '../../utils/fileUrl';
import { downloadExportBlob } from '../../services/api';
import { JsonCodeEditor } from './JsonCodeEditor';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

interface RowDetailsDrawerProps {
  row: Submission;
  columns?: TableColumn[];
  onClose: () => void;
  userTier: RBACPermissionTier;
  onUpdateRow?: (rowId: string, updatedData: Record<string, any>) => Promise<void>;
  onDelete?: () => void;
  onNavigate?: (direction: 'prev' | 'next') => void;
}

export const RowDetailsDrawer: React.FC<RowDetailsDrawerProps> = ({
  row,
  columns,
  onClose,
  userTier,
  onUpdateRow,
  onDelete,
  onNavigate,
}) => {
  const [activeTab, setActiveTab] = useState<'fields' | 'telemetry' | 'json'>('fields');
  const [formData, setFormData] = useState<Record<string, any>>({ ...row.data });
  const [isSaving, setIsSaving] = useState(false);
  const [jsonMode, setJsonMode] = useState<'data' | 'full'>('data');
  const [copiedId, setCopiedId] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [editingJsonFields, setEditingJsonFields] = useState<Record<string, boolean>>({});
  const [jsonFieldErrors, setJsonFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setFormData({ ...row.data });
    setCopiedId(false);
    setCopiedField(null);
    setEditingJsonFields({});
    setJsonFieldErrors({});
  }, [row.id, row.data]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSaving) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, isSaving]);

  const canEdit = userTier === 'edit' || userTier === 'full';

  const handleCopyId = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!row.id) return;
    try {
      await navigator.clipboard.writeText(row.id);
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 1500);
    } catch (err) {
      console.error('Failed to copy ID:', err);
    }
  };

  const handleCopyFieldJson = async (key: string, val: any, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(JSON.stringify(val, null, 2));
      setCopiedField(key);
      setTimeout(() => setCopiedField(null), 1500);
    } catch (err) {
      console.error('Failed to copy field JSON:', err);
    }
  };

  const handleToggleJsonEdit = (key: string) => {
    setEditingJsonFields((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleFieldChange = (key: string, val: string) => {
    setFormData((prev) => ({
      ...prev,
      [key]: val,
    }));
  };

  const hasJsonErrors = Object.keys(jsonFieldErrors).length > 0;

  const handleSaveForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit || !onUpdateRow || hasJsonErrors) return;
    setIsSaving(true);
    try {
      await onUpdateRow(row.id, formData);
    } finally {
      setIsSaving(false);
    }
  };

  const drawerContent = (
    <>
      {/* Backdrop overlay to click-to-dismiss on all screen sizes */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-xs z-[55] animate-in fade-in-50 duration-200 cursor-pointer"
        onClick={() => {
          if (!isSaving) {
            onClose();
          }
        }}
        aria-hidden="true"
      />

      <div
        className="fixed inset-y-0 right-0 !m-0 z-[56] w-full sm:w-[540px] max-w-full bg-cf-surface border-l border-cf-border shadow-2xl flex flex-col animate-in slide-in-from-right duration-200"
        onClick={(e) => e.stopPropagation()}
      >
      {/* Header */}
      <div className="h-14 bg-cf-surface border-b border-cf-border px-5 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Button
            type="button"
            variant="secondary"
            size="xs"
            onClick={handleCopyId}
            title={copiedId ? 'Copied to clipboard!' : `Click to copy ID: ${row.id}`}
            className="font-mono group"
            rightIcon={
              copiedId ? (
                <Check size={12} className="text-emerald-400 shrink-0" />
              ) : (
                <Copy size={12} className="text-cf-textMuted group-hover:text-cf-textPrimary transition-colors shrink-0" />
              )
            }
          >
            <span className="truncate max-w-[130px] sm:max-w-[220px]">{row.id}</span>
          </Button>

          {row.is_spam && (
            <span
              className="px-2 py-0.5 rounded text-xs uppercase font-semibold border bg-rose-500/10 border-rose-500/30 text-rose-400"
            >
              SPAM / QUARANTINE
            </span>
          )}
        </div>

        <div className="flex items-center gap-1">
          {onNavigate && (
            <>
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                onClick={() => onNavigate('prev')}
                title="Previous row"
              >
                <ChevronUp size={15} />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                onClick={() => onNavigate('next')}
                title="Next row"
              >
                <ChevronDown size={15} />
              </Button>
            </>
          )}

          <div className="h-4 w-[1px] bg-cf-border mx-1" />

          {canEdit && onDelete && (
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              onClick={onDelete}
              className="hover:text-rose-400 hover:bg-rose-500/20 mr-1"
              title="Delete row"
            >
              <Trash2 size={15} />
            </Button>
          )}

          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={onClose}
            title="Close drawer (Esc)"
          >
            <X size={16} />
          </Button>
        </div>
      </div>

      {/* Tabs Bar */}
      <div className="flex items-center border-b border-cf-border bg-cf-bg px-4 -mb-px">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setActiveTab('fields')}
          className={`!px-3 !py-2 !rounded-none border-b-2 ${
            activeTab === 'fields'
              ? '!border-cf-orange !text-cf-orange font-semibold'
              : '!border-transparent text-cf-textSecondary hover:text-cf-textPrimary'
          }`}
        >
          Form Fields ({Object.keys(formData).length})
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setActiveTab('telemetry')}
          className={`!px-3 !py-2 !rounded-none border-b-2 ${
            activeTab === 'telemetry'
              ? '!border-cf-orange !text-cf-orange font-semibold'
              : '!border-transparent text-cf-textSecondary hover:text-cf-textPrimary'
          }`}
        >
          Telemetry & Security
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setActiveTab('json')}
          className={`!px-3 !py-2 !rounded-none border-b-2 ${
            activeTab === 'json'
              ? '!border-cf-orange !text-cf-orange font-semibold'
              : '!border-transparent text-cf-textSecondary hover:text-cf-textPrimary'
          }`}
        >
          Raw JSON
        </Button>
      </div>

      {/* Tab Content */}
      <div className="flex-1 p-5 overflow-y-auto">
        {activeTab === 'fields' && (
          <form onSubmit={handleSaveForm} className="space-y-4">
            {Object.keys(formData).length === 0 ? (
              <div className="text-xs text-cf-textMuted italic py-8 text-center">
                This submission payload contains no dynamic fields.
              </div>
            ) : (
              Object.entries(formData).map(([key, val]) => {
                if (isFileAttachment(val) || (Array.isArray(val) && val.length > 0 && isFileAttachment(val[0]))) {
                  const files = Array.isArray(val) ? val : [val];
                  return (
                    <div key={key} className="p-3 rounded-lg border border-cf-border bg-cf-surface/40">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-semibold text-cf-textPrimary flex items-center gap-1.5">
                          <Paperclip size={13} className="text-cf-orange" />
                          <span>{key}</span>
                        </label>
                        <span className="text-xs uppercase font-medium px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                          {files.length > 1 ? `${files.length} Files` : 'File'}
                        </span>
                      </div>
                      <div className="space-y-2">
                        {files.map((fileItem, idx) => {
                          const { gatewayUrl, previewUrl } = resolveFileGatewayUrl(fileItem);
                          const fname = fileItem?.filename || 'Attachment';
                          const fsize = formatFileSize(fileItem?.size_bytes);
                          const ftype = fileItem?.content_type || 'application/octet-stream';
                          const isImg = ftype.startsWith('image/');
                          const isPdf = ftype === 'application/pdf';

                          return (
                            <div
                              key={idx}
                              className="flex items-center justify-between gap-3 p-2.5 rounded border border-cf-border bg-cf-bg hover:border-cf-borderHover transition-colors"
                            >
                              <div className="flex items-center gap-2.5 min-w-0">
                                <div className="p-2 rounded bg-cf-surface border border-cf-border text-cf-orange shrink-0">
                                  {isImg ? <ImageIcon size={15} /> : isPdf ? <FileText size={15} /> : <Paperclip size={15} />}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-xs font-medium text-cf-textPrimary truncate" title={fname}>
                                    {fname}
                                  </p>
                                  <p className="text-xs text-cf-textMuted font-sans tabular-nums flex items-center gap-1.5 mt-0.5">
                                    {fsize && <span>{fsize}</span>}
                                    {fsize && <span>•</span>}
                                    <span className="truncate">{ftype}</span>
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center gap-1.5 shrink-0">
                                <a
                                  href={previewUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="px-2 py-1 rounded bg-cf-surface border border-cf-border hover:bg-cf-surfaceHover text-cf-textSecondary hover:text-cf-textPrimary transition-colors flex items-center gap-1 text-xs"
                                  title="Open file preview in new tab"
                                >
                                  <ExternalLink size={12} />
                                  <span>Open</span>
                                </a>
                                <Button
                                  type="button"
                                  variant="orange-subtle"
                                  size="xs"
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    try {
                                      await downloadExportBlob(gatewayUrl, fname);
                                    } catch (err) {
                                      console.error('Download failed:', err);
                                      window.open(previewUrl, '_blank');
                                    }
                                  }}
                                  leftIcon={<Download size={12} />}
                                  title="Download verified file"
                                >
                                  Download
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }

                if (typeof val === 'object' && val !== null) {
                  const colDef = columns?.find((c) => c.key === key);
                  const colName = colDef?.name || key;
                  const subfields = colDef?.validation_rules?.subfields;
                  const isArr = Array.isArray(val);
                  const count = isArr ? val.length : Object.keys(val).length;
                  const isEditingJson = !!editingJsonFields[key];

                  return (
                    <div key={key} className="p-3.5 rounded-lg border border-cf-border bg-cf-surface/40 space-y-2.5">
                      {/* Field Header */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 overflow-hidden">
                          <label className="text-xs font-semibold text-cf-textPrimary truncate">
                            {colName}
                          </label>
                          {colName !== key && (
                            <span className="text-[10px] font-mono text-cf-textMuted truncate">
                              ({key})
                            </span>
                          )}
                          {isArr ? (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.2 rounded bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/25 font-mono text-[10px] font-semibold shrink-0">
                              <Brackets size={10} />
                              <span>{count} items</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.2 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/25 font-mono text-[10px] font-semibold shrink-0">
                              <span className="font-mono text-[11px] leading-none">{`{ }`}</span>
                              <span>{count} props</span>
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            type="button"
                            variant="secondary"
                            size="xs"
                            onClick={(e) => handleCopyFieldJson(key, val, e)}
                            leftIcon={copiedField === key ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} />}
                            className="!text-[10px]"
                            title="Copy JSON"
                          >
                            {copiedField === key ? 'Copied' : 'JSON'}
                          </Button>

                          {canEdit && (
                            <Button
                              type="button"
                              variant={isEditingJson ? 'orange-subtle' : 'secondary'}
                              size="xs"
                              onClick={() => handleToggleJsonEdit(key)}
                              leftIcon={isEditingJson ? <Eye size={11} /> : <Code size={11} />}
                              className="!text-[10px]"
                              title={isEditingJson ? 'Switch to Inspector View' : 'Edit Raw JSON'}
                            >
                              {isEditingJson ? 'Inspect' : 'Edit JSON'}
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Content: Inspector View vs JSON Code Editor */}
                      {isEditingJson ? (
                        <JsonCodeEditor
                          initialValue={val}
                          subfields={subfields}
                          maxItems={colDef?.validation_rules?.max_items}
                          disabled={!canEdit}
                          minHeight={140}
                          maxHeight={360}
                          onChange={(parsedVal, isValid, errors) => {
                            if (isValid && parsedVal !== null) {
                              setJsonFieldErrors((prev) => {
                                const next = { ...prev };
                                delete next[key];
                                return next;
                              });
                              setFormData((prev) => ({
                                ...prev,
                                [key]: parsedVal,
                              }));
                            } else {
                              setJsonFieldErrors((prev) => ({
                                ...prev,
                                [key]: errors.join('; '),
                              }));
                            }
                          }}
                        />
                      ) : (
                        <div className="space-y-2">
                          {isArr ? (
                            count === 0 ? (
                              <div className="p-3 text-center text-cf-textMuted text-xs italic bg-cf-bg/50 rounded border border-cf-border/60">
                                Empty array
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {val.map((item: any, idx: number) => (
                                  <div key={idx} className="p-2.5 rounded bg-cf-bg border border-cf-border/70 text-xs">
                                    <div className="text-[10px] font-mono text-cf-textMuted uppercase mb-1 font-semibold flex items-center justify-between">
                                      <span>Item #{idx + 1}</span>
                                    </div>
                                    {typeof item === 'object' && item !== null ? (
                                      <div className="grid grid-cols-2 gap-2 pt-1">
                                        {Object.entries(item).map(([subK, subV]) => {
                                          const subDef = subfields?.find((s) => s.key === subK);
                                          const subLabel = subDef?.name || subK;
                                          return (
                                            <div key={subK} className="overflow-hidden">
                                              <span className="text-[10px] text-cf-textMuted block font-mono capitalize truncate">
                                                {subLabel}
                                              </span>
                                              <span className="text-xs text-cf-textPrimary font-medium truncate block select-text">
                                                {typeof subV === 'object' ? JSON.stringify(subV) : String(subV ?? '—')}
                                              </span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ) : (
                                      <div className="text-cf-textPrimary font-mono text-xs select-text">
                                        {String(item)}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )
                          ) : (
                            count === 0 ? (
                              <div className="p-3 text-center text-cf-textMuted text-xs italic bg-cf-bg/50 rounded border border-cf-border/60">
                                Empty object
                              </div>
                            ) : (
                              <div className="rounded border border-cf-border bg-cf-bg divide-y divide-cf-border/60 text-xs overflow-hidden">
                                {Object.entries(val).map(([subK, subV]) => {
                                  const subDef = subfields?.find((s) => s.key === subK);
                                  const subLabel = subDef?.name || subK.replace(/_/g, ' ');
                                  return (
                                    <div
                                      key={subK}
                                      className="flex items-center justify-between py-2 px-3 hover:bg-cf-surfaceHover/30 transition-colors gap-3"
                                    >
                                      <div className="flex items-center shrink-0 max-w-[160px]">
                                        <span className="text-xs font-medium text-cf-textPrimary">
                                          {subLabel}
                                        </span>
                                      </div>
                                      <div className="text-right text-xs max-w-[65%] break-words text-cf-textSecondary select-text">
                                        {subV === null || subV === undefined ? (
                                          <span className="text-zinc-600 italic">null</span>
                                        ) : typeof subV === 'boolean' ? (
                                          <span
                                            className={`inline-block px-1.5 py-0.2 rounded text-[10px] font-bold ${
                                              subV
                                                ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                                                : 'bg-cf-surfaceActive text-cf-textMuted border border-cf-border'
                                            }`}
                                          >
                                            {subV ? 'TRUE' : 'FALSE'}
                                          </span>
                                        ) : typeof subV === 'object' ? (
                                          <pre className="font-mono text-[10px] text-left bg-cf-surface p-1 rounded border border-cf-border overflow-x-auto">
                                            {JSON.stringify(subV, null, 2)}
                                          </pre>
                                        ) : (
                                          <span className="font-mono text-[11px] text-cf-textPrimary">{String(subV)}</span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  );
                }

                const colDef = columns?.find((c) => c.key === key);
                const colName = colDef?.name || key;

                return (
                  <div key={key}>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-xs font-medium text-cf-textSecondary">
                        {colName}
                      </label>
                      {colName !== key && (
                        <span className="text-[10px] font-mono text-cf-textMuted">
                          {key}
                        </span>
                      )}
                    </div>
                    <Input
                      size="sm"
                      type="text"
                      disabled={!canEdit}
                      value={String(val ?? '')}
                      onChange={(e) => handleFieldChange(key, e.target.value)}
                    />
                  </div>
                );
              })
            )}

            {canEdit && onUpdateRow && Object.keys(formData).length > 0 && (
              <div className="pt-3 border-t border-cf-border flex items-center justify-between">
                {hasJsonErrors ? (
                  <span className="text-xs text-rose-500 font-medium">
                    Please fix JSON syntax errors before saving.
                  </span>
                ) : (
                  <div />
                )}
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  disabled={isSaving || hasJsonErrors}
                  isLoading={isSaving}
                  leftIcon={!isSaving ? <Save size={13} /> : undefined}
                >
                  Save Changes
                </Button>
              </div>
            )}
          </form>
        )}

        {activeTab === 'telemetry' && (
          <div className="space-y-4 text-xs">
            {/* Honeypot & Spam Analysis Card */}
            <div
              className={`p-3.5 rounded-md border ${
                row.is_spam
                  ? 'bg-rose-500/10 border-rose-500/30 text-rose-700 dark:text-rose-300'
                  : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-1.5 font-semibold">
                {row.is_spam ? <ShieldAlert size={16} /> : <ShieldCheck size={16} />}
                <span>{row.is_spam ? 'Status: SPAM (Quarantined)' : 'Status: OK (Clean)'}</span>
              </div>
              <div className="text-xs leading-relaxed opacity-90">
                {row.honeypot_triggered ? (
                  <>Honeypot field was populated by bot. Ingestion was quarantined silently.</>
                ) : (
                  <>Honeypot traps were passed cleanly. Asynchronous notifications dispatched.</>
                )}
                {row.spam_reason && <div>Reason: {row.spam_reason}</div>}
              </div>
            </div>

            {/* Ingestion Latency Card */}
            <div className="p-3.5 rounded-md bg-cf-surface border border-cf-border space-y-2">
              <div className="flex items-center gap-2 text-cf-textPrimary font-semibold">
                <Clock size={15} className="text-cf-orange" />
                <span>Ingestion Latency</span>
              </div>
              <div className="flex items-center justify-between text-cf-textSecondary">
                <span>Synchronous Processing Time:</span>
                <span className="text-emerald-400 font-bold tabular-nums">
                  {(() => {
                    const lat = row.metadata?.latency_ms ?? row.metadata?.processing_time_ms;
                    return typeof lat === 'number' && lat > 0
                      ? `${lat.toFixed(2)} ms`
                      : '—';
                  })()}
                </span>
              </div>
              <div className="flex items-center justify-between text-cf-textSecondary">
                <span>Created At:</span>
                <span className="text-cf-textPrimary tabular-nums">
                  {row.created_at ? new Date(row.created_at).toISOString().replace('T', ' ').slice(0, 19) : '—'}
                </span>
              </div>
            </div>

            {/* Client Telemetry Card */}
            <div className="p-3.5 rounded-md bg-cf-surface border border-cf-border space-y-2.5">
              <div className="flex items-center gap-2 text-cf-textPrimary font-semibold">
                <Globe size={15} className="text-cf-orange" />
                <span>Client Telemetry</span>
              </div>

              <div>
                <span className="text-cf-textSecondary block text-xs">IP Address:</span>
                <span className="text-cf-textPrimary">{row.metadata?.ip || '127.0.0.1'}</span>
              </div>

              <div>
                <span className="text-cf-textSecondary block text-xs">User-Agent:</span>
                <span className="text-cf-textPrimary break-all">
                  {row.metadata?.user_agent || 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)'}
                </span>
              </div>

              <div>
                <span className="text-cf-textSecondary block text-xs">Referer / Origin:</span>
                <span className="text-cf-textPrimary">
                  {row.metadata?.referer || row.metadata?.origin || 'Direct Webhook'}
                </span>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'json' && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs">
              <Button
                type="button"
                variant={jsonMode === 'data' ? 'orange-subtle' : 'ghost'}
                size="xs"
                onClick={() => setJsonMode('data')}
              >
                Data Payload Only
              </Button>
              <Button
                type="button"
                variant={jsonMode === 'full' ? 'orange-subtle' : 'ghost'}
                size="xs"
                onClick={() => setJsonMode('full')}
              >
                Full Database Record
              </Button>
            </div>

            <RawJSONViewer
              data={jsonMode === 'data' ? row.data : row}
              title={jsonMode === 'data' ? 'Form Data (JSONB)' : 'Full Row Record'}
            />
          </div>
        )}
      </div>
    </div>
    </>
  );

  return typeof document !== 'undefined' ? createPortal(drawerContent, document.body) : drawerContent;
};
